import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import {DateTime} from 'luxon';
import {useNavigate} from 'react-router';

import {KYC_IN_REVIEW_IDV_SUBMITTED_EVENTS} from '@/app/auth/events/KycIdvSubmittedEvents';
import {useIdvSubmittedTrackers} from '@/app/main/onboarding/pages/kyc/pages/KycInReview/pages/IdvSubmitted/trackers';

//#region [---- [TYPES] ----]
type ViewModelContextType = {
  validationStep: number;
  isInfoCardVisible: boolean;
  onPressCloseAndNotifyMe: () => void;
  onPressIWillWait: () => void;
};
//#endregion

//#region [---- [CONSTANTS] ----]
const STORAGE_IDV_SUBMITTED_TIME = 'idv-submitted-process-time';
const NOTIFY_ME_ROUTE = '/onboarding/notify-me?previousScreen=idv-submitted';
const FIRST_MESSAGE_TIME = 1;
const SECOND_MESSAGE_TIME = 2;
const MINUTE = 60000;
//#endregion

const ViewModelContext = createContext<ViewModelContextType | undefined>(
  undefined,
);

export function ViewModelProvider({children}: {children: ReactNode}) {
  //#region [---- [STATE] ----]
  const [validationStep, setValidationStep] = useState<number>(0);
  const [isInfoCardVisible, setIsInfoCardVisible] = useState(true);
  //#endregion

  //#region [---- [HOOKS] ----]
  const navigate = useNavigate();
  const {
    trackIdvSubmitted,
    selectKycIdvProcessingWait,
    selectKycIdvProcessingNotifyMe,
  } = useIdvSubmittedTrackers();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  //#endregion

  //#region [---- [EFFECTS] ----]
  useEffect(() => {
    trackIdvSubmitted(
      KYC_IN_REVIEW_IDV_SUBMITTED_EVENTS.VIEW_KYC_IN_REVIEW_IDV_SUBMITTED_PAGE,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  //#endregion

  //#region [---- [HELPERS] ----]
  const checkAndUpdateMessage = useCallback(() => {
    const timeOutStorage = localStorage.getItem(STORAGE_IDV_SUBMITTED_TIME);
    if (!timeOutStorage) return;

    const processTime = DateTime.fromISO(JSON.parse(timeOutStorage));
    const now = DateTime.local();

    const firstMomentElapsed =
      now.diff(processTime, 'minutes').minutes >= FIRST_MESSAGE_TIME;
    const secondMomentElapsed =
      now.diff(processTime, 'minutes').minutes >= SECOND_MESSAGE_TIME;

    if (secondMomentElapsed) {
      setValidationStep(2);
      trackIdvSubmitted(
        KYC_IN_REVIEW_IDV_SUBMITTED_EVENTS.VIEW_KYC_IN_REVIEW_IDV_SUBMITTED_SECOND_MESSAGE,
      );
    } else if (firstMomentElapsed) {
      setValidationStep(1);
      trackIdvSubmitted(
        KYC_IN_REVIEW_IDV_SUBMITTED_EVENTS.VIEW_KYC_IN_REVIEW_IDV_SUBMITTED_FIRST_MESSAGE,
      );
    } else {
      setValidationStep(0);
    }

    timeoutRef.current = setTimeout(checkAndUpdateMessage, MINUTE);
  }, [trackIdvSubmitted]);

  const getTimeMessagesFromStorage = useCallback(() => {
    let timeOutStorage = localStorage.getItem(STORAGE_IDV_SUBMITTED_TIME);

    if (!timeOutStorage) {
      timeOutStorage = JSON.stringify(new Date());
      localStorage.setItem(STORAGE_IDV_SUBMITTED_TIME, timeOutStorage);
    }

    checkAndUpdateMessage();
  }, [checkAndUpdateMessage]);
  //#endregion

  //#region [---- [EFFECTS] ----]
  useEffect(() => {
    getTimeMessagesFromStorage();

    return () => {
      if (timeoutRef?.current) {
        clearTimeout(timeoutRef?.current);
      }
    };
  }, [getTimeMessagesFromStorage]);
  //#endregion

  //#region [---- [LOGIC] ----]
  const onPressCloseAndNotifyMe = useCallback(() => {
    selectKycIdvProcessingNotifyMe(
      KYC_IN_REVIEW_IDV_SUBMITTED_EVENTS.SELECT_KYC_IDV_PROCESSING_NOTIFY_ME,
    );
    navigate(NOTIFY_ME_ROUTE);
  }, [navigate, selectKycIdvProcessingNotifyMe]);

  const onPressIWillWait = useCallback(() => {
    selectKycIdvProcessingWait(
      KYC_IN_REVIEW_IDV_SUBMITTED_EVENTS.SELECT_KYC_IDV_PROCESSING_WAIT,
    );
    setIsInfoCardVisible(false);
  }, [selectKycIdvProcessingWait]);
  //#endregion

  //#region [---- [CONTEXT VALUE] ----]
  const value = {
    validationStep,
    isInfoCardVisible,
    onPressCloseAndNotifyMe,
    onPressIWillWait,
  };
  //#endregion

  return (
    <ViewModelContext.Provider value={value}>
      {children}
    </ViewModelContext.Provider>
  );
}

export function useViewModelProvider() {
  const context = useContext(ViewModelContext);
  if (context === undefined) {
    throw new Error('IdvSubmitted ViewModelContext is undefined');
  }
  return context;
}
